import React, { useState, useEffect } from 'react';
import { EXTERNAL_LINKS } from '../constants';
import '../styles/MovieDetailsModal.css';

const MovieDetailsModal = ({ movie, isOpen, onClose, onBookingClick, onShareClick }) => {
    const [selectedShowtime, setSelectedShowtime] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setSelectedShowtime(null);
        }
    }, [isOpen]);

    if (!isOpen || !movie) return null;

    const handleShowtimeClick = (showtime) => {
        window.plausible?.('ClickTimetable', {
            props: {
                movie_title: movie.original_title,
                cinema: showtime.cinema_name,
                showtime: showtime.start_time
            }
        });
        setSelectedShowtime(showtime);
    };

    const handleBookClick = () => {
        if (selectedShowtime?.theater_booking_link) {
            // Call parent prop
            onBookingClick?.(movie, selectedShowtime, selectedShowtime.theater_booking_link);

            window.open(selectedShowtime.theater_booking_link, '_blank', 'noopener,noreferrer');
        }
        setSelectedShowtime(null);
    };

    const handleShareClick = () => {
        if (selectedShowtime) {
            // Call parent prop
            onShareClick?.(movie, selectedShowtime);

            const url = selectedShowtime.theater_booking_link;
            if (navigator.share) {
                navigator.share({
                    title: movie.original_title,
                    text: `Let's watch ${movie.original_title} at ${selectedShowtime.cinema_name} at ${selectedShowtime.start_time.substring(0,5)}!`,
                    url
                }).catch(console.error);
            } else {
                navigator.clipboard.writeText(url);
                alert('Link copied to clipboard!');
            }
            setSelectedShowtime(null);
        }
    };

    const uniqueCinemas = movie.showtimes
        ? [...new Set(movie.showtimes.map((st) => st.cinema_name))]
        : [];

    const showtimesFor = (cinema) => {
        if (!movie.showtimes) return [];
        const cinemaShowtimes = movie.showtimes.filter(st => st.cinema_name === cinema);
        const uniqueShowtimes = {};
        cinemaShowtimes.forEach(showtime => {
            const timeKey = showtime.start_time.substring(0, 5);
            if (!uniqueShowtimes[timeKey]) {
                uniqueShowtimes[timeKey] = showtime;
            }
        });
        return Object.values(uniqueShowtimes).sort((a, b) =>
            a.start_time.localeCompare(b.start_time)
        );
    };

    return (
        <div className="movie-modal-overlay" onClick={onClose}>
            <div className="movie-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="movie-modal-close" onClick={onClose}>×</button>

                <div className="movie-modal-header">
                    <h2 className="movie-modal-title">
                        {movie.original_title || 'Unknown Title'}
                        {movie.year_of_release && (
                            <span className="movie-modal-year"> ({movie.year_of_release})</span>
                        )}
                    </h2>
                    <div className="movie-modal-details">
                        {movie.duration && <span>{movie.duration}min</span>}
                        {movie.rotten_tomatoes_rating != null && (
                            <span className="rating-item">🍅 {movie.rotten_tomatoes_rating}</span>
                        )}
                        {movie.imdb_rating != null && (
                            <span className="rating-item">IMDb {movie.imdb_rating}</span>
                        )}
                        {movie.director && <span>Director: {movie.director}</span>}
                        {movie.genres?.length > 0 && <span>{movie.genres.join(', ')}</span>}
                    </div>
                </div>

                {movie.trailer_url && (
                    <div className="movie-modal-trailer">
                        <a
                            href={movie.trailer_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="trailer-button"
                        >
                            ▶ Watch Trailer
                        </a>
                    </div>
                )}

                {uniqueCinemas.length > 0 && (
                    <div className="movie-modal-showtimes">
                        <div className="showtime-instruction">
                            Click on time to book or share
                        </div>

                        {uniqueCinemas.map((cinemaName) => {
                            const showtimes = showtimesFor(cinemaName);
                            const sanitized = cinemaName
                                .toLowerCase()
                                .replace(/\s+/g, '')
                                .replace(/\//g, '');
                            const logoUrl = `${EXTERNAL_LINKS.MEDIA_BASE}${sanitized}.png`;
                            const cinemaUrl = movie.showtimes.find(
                                (s) => s.cinema_name === cinemaName
                            )?.cinema_url;

                            if (showtimes.length === 0) return null;

                            return (
                                <div key={cinemaName} className="cinema-showtime-row">
                                    <div className="cinema-info">
                                        <a
                                            href={cinemaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="cinema-logo-link"
                                        >
                                            <img
                                                src={logoUrl}
                                                alt={`${cinemaName} logo`}
                                                className="cinema-logo"
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        </a>
                                        <span className="cinema-name">{cinemaName}</span>
                                    </div>

                                    <div className="showtime-list">
                                        {showtimes.map((showtime) => (
                                            <button
                                                key={showtime.start_time}
                                                className="showtime-chip clickable"
                                                onClick={() => handleShowtimeClick(showtime)}
                                                title={`Book or share ${showtime.start_time.substring(0, 5)} at ${cinemaName}`}
                                            >
                                                {showtime.start_time.substring(0, 5)}
                                                <span className="showtime-arrow">↗</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {movie.description && (
                    <div className="movie-modal-description">
                        <p>{movie.description}</p>
                    </div>
                )}
            </div>

            {selectedShowtime && (
                <div className="showtime-action-popup-overlay" onClick={() => setSelectedShowtime(null)}>
                    <div className="showtime-action-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="popup-content">
                            <h3>{movie.original_title}</h3>
                            <p>{selectedShowtime.cinema_name}, {selectedShowtime.start_time.substring(0,5)}</p>

                            <button onClick={handleBookClick} className="popup-button">
                                Book on cinema site
                            </button>

                            <button onClick={handleShareClick} className="popup-button">
                                Share this showtime
                            </button>

                            <button onClick={() => setSelectedShowtime(null)} className="popup-cancel">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MovieDetailsModal;
